import React, { useState, useMemo } from 'react';
import { Loader2, Tag, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as api from '@/services/seasonalWmsApi';

// Correct a box's slug so the WMS matches Halnode. Two modes: move the whole box,
// or split part of its qty into a new box under the target slug.
export const ReslugBoxDialog = ({ box, knownSlugs = [], onClose, onDone }) => {
  const lines = (box?.items || []).filter((it) => (it.barcode || '') && (it.qty || 0) > 0);
  const [mode, setMode] = useState('move'); // 'move' | 'split'
  const [target, setTarget] = useState('');
  const [qty, setQty] = useState({}); // barcode -> qtyString to MOVE
  const [busy, setBusy] = useState(false);

  const num = (v) => { const n = parseInt(v, 10); return Number.isFinite(n) && n > 0 ? n : 0; };
  const avail = useMemo(() => {
    const m = {}; lines.forEach((it) => { m[it.barcode] = (m[it.barcode] || 0) + Number(it.qty || 0); }); return m;
  }, [lines]);
  const over = lines.some((it) => num(qty[it.barcode]) > (avail[it.barcode] || 0));
  const movedTotal = lines.reduce((s, it) => s + num(qty[it.barcode]), 0);
  const suggestions = (knownSlugs || []).filter((s) => s && s !== (box?.slug || ''));

  const submit = async () => {
    const t = target.trim();
    if (!t) { toast.error('Enter a target slug'); return; }
    if (t === (box?.slug || '')) { toast.error('Target slug is the same as the current slug'); return; }
    if (mode === 'split') {
      if (movedTotal <= 0) { toast.error('Enter a quantity to move'); return; }
      if (over) { toast.error('A quantity exceeds what is available'); return; }
    }
    const payload = {
      box_number: box.box_number, target_slug: t, mode,
      lines: mode === 'split'
        ? Object.entries(qty).map(([barcode, q]) => ({ barcode, qty: num(q) })).filter((l) => l.qty > 0)
        : null,
    };
    setBusy(true);
    try {
      const r = await api.reslugBox(payload);
      toast.success(r.message);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-slug failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-xl" data-testid="ssr-reslug-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-indigo-600" /> Re-slug {box?.box_number}
          </DialogTitle>
          <DialogDescription>Fix the slug so the WMS matches Halnode.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-xs text-[#5A5F6E]">
            Current slug <b>{box?.slug || '—'}</b> · {box?.total_qty} pcs. Fix the slug so the WMS matches Halnode.
          </p>

          <div className="flex gap-2">
            <button
              onClick={() => setMode('move')}
              className={`flex-1 text-xs rounded-md border px-3 py-2 ${mode === 'move' ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold' : 'border-gray-200 text-gray-600'}`}
              data-testid="ssr-reslug-mode-move">Move whole box</button>
            <button
              onClick={() => setMode('split')}
              className={`flex-1 text-xs rounded-md border px-3 py-2 ${mode === 'split' ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold' : 'border-gray-200 text-gray-600'}`}
              data-testid="ssr-reslug-mode-split">Split part to another slug</button>
          </div>

          <div>
            <label className="text-xs text-[#5A5F6E]">Target slug <span className="text-red-500">*</span></label>
            <Input list="reslug-slug-suggestions" value={target} onChange={(e) => setTarget(e.target.value)}
              placeholder="e.g. SPY" className="uppercase" data-testid="ssr-reslug-target-input" />
            <datalist id="reslug-slug-suggestions">
              {suggestions.map((s) => <option key={s} value={s} />)}
            </datalist>
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => setTarget(s)}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 hover:bg-indigo-100 text-gray-700"
                    data-testid={`ssr-reslug-suggest-${s}`}>{s}</button>
                ))}
              </div>
            )}
          </div>

          {mode === 'split' && (
            <div className="overflow-x-auto rounded-md border border-[#EFE9DC]">
              <table className="w-full text-xs" data-testid="ssr-reslug-grid">
                <thead className="bg-[#F5F1E8] text-[#5A5F6E]">
                  <tr><th className="text-left px-2 py-1.5 font-medium">SKU</th><th className="px-2 text-right">Avail</th><th className="px-2 text-center">Move</th><th className="px-2 text-right">Stays</th></tr>
                </thead>
                <tbody className="divide-y divide-[#F0EADD] bg-white">
                  {lines.map((it) => (
                    <tr key={it.barcode}>
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{it.design_name || it.description || '—'}</div>
                        <div className="text-[10px] text-[#8A8F9E] font-mono">{it.barcode} · {it.shade || '—'} · {it.size || '—'}</div>
                      </td>
                      <td className="px-2 text-right tabular-nums font-semibold">{avail[it.barcode] || 0}</td>
                      <td className="px-1 py-1">
                        <Input type="number" min="0" value={qty[it.barcode] ?? ''}
                          onChange={(e) => setQty((p) => ({ ...p, [it.barcode]: e.target.value }))}
                          className="h-8 text-center px-1" data-testid={`ssr-reslug-input-${it.barcode}`} />
                      </td>
                      <td className={`px-2 text-right tabular-nums font-semibold ${num(qty[it.barcode]) > (avail[it.barcode] || 0) ? 'text-red-600' : 'text-[#2D5F4F]'}`}>
                        {(avail[it.barcode] || 0) - num(qty[it.barcode])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-[#5A5F6E] flex items-center gap-1.5" data-testid="ssr-reslug-summary">
            <ArrowRightLeft className="w-3.5 h-3.5" />
            {mode === 'move'
              ? <>Whole box → slug <b>{target.trim() || '…'}</b></>
              : <>Moving <b>{movedTotal}</b> pcs into a new box under slug <b>{target.trim() || '…'}</b>; the rest stays in {box?.box_number}</>}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} data-testid="ssr-reslug-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !target.trim() || (mode === 'split' && (over || movedTotal <= 0))}
            className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ssr-reslug-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Tag className="w-4 h-4 mr-2" />} Re-slug
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReslugBoxDialog;
